import React, { useState } from "react";
import { Phone, FileText, Download, ExternalLink, X, ArrowUpRight } from "lucide-react";
import {
  PHONE,
  PHONE_TEL,
  MEDICAL_LIST_PDF,
  ENROLLMENT_FORM_PDF,
  CAZIER_BRASOV_URL,
} from "../lib/assets";
import { useLang } from "../context/LangContext";

const LINK_MAP = {
  MEDICAL_LIST_PDF,
  ENROLLMENT_FORM_PDF,
};

const EXTERNAL_MAP = {
  CAZIER_BRASOV_URL,
};

const PDF_TITLES = {
  MEDICAL_LIST_PDF: "Lista unităților medicale — județul Brașov",
  ENROLLMENT_FORM_PDF: "Cerere de înscriere — BIU BIU DRIVING",
};

const Documents = () => {
  const { t } = useLang();
  const [preview, setPreview] = useState(null); // { url, title }

  return (
    <section id="docs" data-testid="docs-section" className="py-24 sm:py-32 bg-brand-cream">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 mb-14">
          <div className="lg:col-span-7">
            <p className="text-xs sm:text-sm uppercase tracking-[0.22em] font-bold text-brand-orange mb-4">
              {t.docs.eyebrow}
            </p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-navy leading-tight tracking-tight">
              {t.docs.title}
            </h2>
            <p className="mt-5 text-base sm:text-lg text-brand-muted leading-relaxed max-w-2xl">
              {t.docs.subtitle}
            </p>
          </div>

          <div className="lg:col-span-5 flex lg:items-end">
            <div className="bg-brand-navy text-white rounded-3xl p-6 sm:p-7 w-full">
              <p className="text-sm text-white/80 mb-3">{t.docs.note}</p>
              <a
                href={`tel:${PHONE_TEL}`}
                data-testid="docs-phone-cta"
                className="inline-flex items-center gap-2 bg-brand-orange hover:bg-brand-orange-deep text-white font-semibold px-5 py-3 rounded-full transition-colors"
              >
                <Phone className="w-4 h-4" />
                {PHONE}
              </a>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {t.docs.items.map((item, i) => {
            const pdfUrl = item.link ? LINK_MAP[item.link] : null;
            const externalUrl = item.externalUrl ? EXTERNAL_MAP[item.externalUrl] : null;
            const isPdf = !!pdfUrl;
            const isExt = !!externalUrl;

            const numberOrIcon = (isPdf || isExt) ? (
              <FileText className="w-5 h-5" />
            ) : (
              String(i + 1).padStart(2, "0")
            );

            const innerContent = (
              <div className="flex items-start gap-4">
                <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center font-display font-bold text-lg transition-colors ${
                  isPdf
                    ? "bg-brand-navy text-white group-hover:bg-brand-orange group-hover:text-white"
                    : "bg-brand-orange/15 text-brand-orange-deep group-hover:bg-brand-orange group-hover:text-white"
                }`}>
                  {numberOrIcon}
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-lg font-bold text-brand-navy">{item.t}</h3>
                  <p className="mt-1 text-sm text-brand-muted leading-relaxed">{item.d}</p>

                  {isPdf && (
                    <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-brand-orange-deep group-hover:gap-2.5 transition-all">
                      <Download className="w-3.5 h-3.5" />
                      {item.linkLabel}
                    </span>
                  )}

                  {isExt && (
                    <span className="mt-3 inline-flex items-start gap-1.5 text-xs font-semibold text-brand-orange-deep group-hover:gap-2.5 transition-all leading-snug">
                      <ArrowUpRight className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      <span className="break-words">{item.externalLabel}</span>
                    </span>
                  )}
                </div>
              </div>
            );

            if (isPdf) {
              return (
                <button
                  key={item.t}
                  type="button"
                  onClick={() => setPreview({ url: pdfUrl, title: PDF_TITLES[item.link] || item.t })}
                  data-testid={`docs-item-${i}`}
                  className="group text-left bg-white border-2 border-brand-orange/40 hover:border-brand-orange rounded-2xl p-6 lift-on-hover cursor-pointer relative overflow-hidden"
                >
                  <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-brand-orange/10 text-brand-orange-deep text-[10px] uppercase tracking-wider font-bold">
                    PDF
                  </span>
                  {innerContent}
                </button>
              );
            }

            if (isExt) {
              return (
                <a
                  key={item.t}
                  href={externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`docs-item-${i}`}
                  className="group block bg-white border-2 border-brand-navy/30 hover:border-brand-navy rounded-2xl p-6 lift-on-hover relative overflow-hidden"
                >
                  <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-brand-navy/10 text-brand-navy text-[10px] uppercase tracking-wider font-bold">
                    Link
                  </span>
                  {innerContent}
                </a>
              );
            }

            return (
              <div
                key={item.t}
                data-testid={`docs-item-${i}`}
                className="group bg-white border border-brand-border-soft rounded-2xl p-6 lift-on-hover"
              >
                {innerContent}
              </div>
            );
          })}
        </div>
      </div>

      {/* PDF Preview Modal */}
      {preview && (
        <div
          data-testid="docs-pdf-modal"
          className="fixed inset-0 z-[100] bg-brand-navy-deep/85 backdrop-blur-sm p-4 sm:p-8 flex flex-col"
          onClick={(e) => {
            if (e.target === e.currentTarget) setPreview(null);
          }}
        >
          <div className="flex items-center justify-between mb-3 max-w-6xl mx-auto w-full">
            <p className="font-display text-white text-sm sm:text-base font-semibold truncate pr-3">
              {preview.title}
            </p>
            <div className="flex items-center gap-2">
              <a
                href={preview.url}
                download
                target="_blank"
                rel="noopener noreferrer"
                data-testid="docs-pdf-download"
                className="inline-flex items-center gap-2 bg-brand-orange hover:bg-brand-orange-deep text-white font-semibold px-4 py-2 rounded-full text-sm transition-colors"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Descarcă</span>
              </a>
              <a
                href={preview.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="docs-pdf-open"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-4 py-2 rounded-full text-sm transition-colors"
                aria-label="Deschide în tab nou"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
              <button
                type="button"
                onClick={() => setPreview(null)}
                data-testid="docs-pdf-close"
                className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                aria-label="Închide"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="flex-1 max-w-6xl mx-auto w-full bg-white rounded-2xl overflow-hidden shadow-2xl">
            <iframe
              src={`https://docs.google.com/gview?url=${encodeURIComponent(preview.url)}&embedded=true`}
              title={preview.title}
              className="w-full h-full"
              data-testid="docs-pdf-iframe"
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default Documents;
